import http, { handleApiResponse, handleApiResponseWithSuccess } from "@/services/http"
import ApiResponse from "@/models/ApiResponse"
import IfaceSyncInterface from "../models/IfaceSyncInterface"
import IfaceSyncInterfaceType from "../models/IfaceSyncInterfaceType"
import IfaceSyncFeature from "../models/IfaceSyncFeature"
import IfaceSyncStatus from "../models/IfaceSyncStatus"
import IfaceSyncPartnerDTO from "../models/IfaceSyncPartnerDTO"
import IfaceSyncInvoiceHeadDTO from "../models/IfaceSyncInvoiceHeadDTO"
import GeneralGetPurchaseInvoicesRequestDTO from "../models/GeneralGetPurchaseInvoicesRequestDTO"
import IfaceSyncDictionaryOption from "../models/IfaceSyncDictionaryOption"
import Country from "@/modules/cashPoint/models/Country"
import { IfaceType } from "../models/IfaceType"
import { Iface } from "../models/Iface"
import { withBusyIndication } from "@/utils/busyIndication"

export default class InterfaceApi {

  static getLiveInterfaces(): Promise<IfaceSyncInterface[]> {
    return http
      .get<ApiResponse<IfaceSyncInterface[]>>(`iface-sync-interface/live`)
      .then(handleApiResponse);
  }

  static getLiveInterfaceTypes = withBusyIndication(
    (): Promise<IfaceSyncInterfaceType[]> =>
      http
        .get<ApiResponse<IfaceSyncInterfaceType[]>>(`iface-sync-interface-type/live`)
        .then(handleApiResponse)
  );

  /** Az interfész típusok láthatóságai és engedélyezett funkciói (rssysisyncfeature) */
  static getLiveFeatures(): Promise<IfaceSyncFeature[]> {
    return http
      .get<ApiResponse<IfaceSyncFeature[]>>(`iface-sync-feature`)
      .then(handleApiResponse);
  }

  static getLiveSyncStatuses(): Promise<IfaceSyncStatus[]> {
    return http
      .get<ApiResponse<IfaceSyncStatus[]>>(`iface-sync-status/live`)
      .then(handleApiResponse);
  }

  /** Egy interfész adott törzsre vonatkozó mappelései (külső kód + K2D megnevezés) */
  static getDictionaryOptions(interfaceId: number, tableName: string): Promise<IfaceSyncDictionaryOption[]> {
    return http
      .get<ApiResponse<IfaceSyncDictionaryOption[]>>(`iface-sync-dictionary/options/${interfaceId}/${tableName}`)
      .then(handleApiResponse);
  }

  /** Egy K2D partner külső kódja az adott interfészen (a partner választóhoz) */
  static getPartnerExtCode(interfaceId: number, partnerId: number): Promise<string> {
    return http
      .get<ApiResponse<string>>(`iface-sync-partner/ext-code/${interfaceId}/${partnerId}`)
      .then(handleApiResponse);
  }

  /** K2D pénznemek — a részletes szerkesztő pénznem választójának forrása */
  static getCurrencies(): Promise<Country[]> {
    return http
      .get<ApiResponse<Country[]>>(`currency`)
      .then(handleApiResponse);
  }

  /** Egy szinkron partner rekord betöltése ID alapján (a részletes szerkesztőhöz) */
  static getPartnerById(id: number): Promise<IfaceSyncPartnerDTO> {
    return http
      .get<ApiResponse<IfaceSyncPartnerDTO>>(`iface-sync-partner/${id}`)
      .then(handleApiResponse);
  }

  /** Egy partner külső azonosítójához tartozó összes szinkron előzmény */
  static getPartnerHistory(interfaceId: number, extCode: string): Promise<IfaceSyncPartnerDTO[]> {
    return http
      .get<ApiResponse<IfaceSyncPartnerDTO[]>>(`iface-sync-partner/history/${interfaceId}/${encodeURIComponent(extCode)}`)
      .then(handleApiResponse);
  }

  /** Egy számlafej külső azonosítójához tartozó összes szinkron előzmény */
  static getInvoiceHeadHistory(interfaceId: number, extCode: string): Promise<IfaceSyncInvoiceHeadDTO[]> {
    return http
      .get<ApiResponse<IfaceSyncInvoiceHeadDTO[]>>(`iface-sync-invoice-head/history/${interfaceId}/${encodeURIComponent(extCode)}`)
      .then(handleApiResponse);
  }

  /** Egy szinkron számlafej rekord betöltése ID alapján (a részletes szerkesztőhöz) */
  static getInvoiceHeadById(id: number): Promise<IfaceSyncInvoiceHeadDTO> {
    return http
      .get<ApiResponse<IfaceSyncInvoiceHeadDTO>>(`iface-sync-invoice-head/${id}`)
      .then(handleApiResponse);
  }

  /**
   * Kijelölt elemek down-syncje. A teljes (módosításokkal együtt szerkesztett) DTO-kat
   * küldjük — a backend a `UserModified` flag alapján partícionál:
   *  - module módosított sorok → új köztes tábla sor (status 2, UserModified=true; számlánál a tételek is)
   *  - nem módosított sorok → az ID-ket továbbadja az IfaceSync API-nak újra-lekérésre
   * `withBusyIndication` — a hívás idejére globális busy indicator villog.
   */
  static downSyncSelectedItems = withBusyIndication(
    (items: any[], iface: Iface, ifaceType: IfaceType): Promise<void> =>
      http
        .post<ApiResponse<void>>(`iface-sync/down-sync/selected-items`, { items, iface, ifaceType })
        .then((res) => handleApiResponseWithSuccess(res, "Kijelölt tételek szinkronizálása elindítva"))
  );

  /**
   * Interfész down-sync a megadott időintervallumra az IfaceSync API-n keresztül.
   * A backend az `iface` szerint választja ki a base URL utáni path szegmenst, az `ifaceType`
   * pedig az azt követő végpont szegmenst.
   * `withBusyIndication` — a hívás idejére globális busy indicator villog.
   */
  static downSyncInterface = withBusyIndication(
    (dateFrom: Date, dateTo: Date, iface: Iface, ifaceType: IfaceType): Promise<void> =>
      http
        .post<ApiResponse<void>>(`iface-sync/down-sync/interface`, { dateFrom, dateTo, iface, ifaceType })
        .then((res) => handleApiResponseWithSuccess(res, "Interfész szinkronizálása elindítva"))
  );

  /**
   * Down-sync indítása az interfész dedikált down-sync végpontján (pl. Incadea: invoice/downsync).
   * Szándékosan NINCS `withBusyIndication`: a hívó (pénztár számlaválogató) a szinkront és az azt
   * követő lista frissítést egyetlen busy indicator alá fogja.
   */
  static downSync(dateFrom: Date, dateTo: Date, iface: Iface, ifaceType: IfaceType): Promise<void> {
    return http
      .post<ApiResponse<void>>(`iface-sync/down-sync`, { dateFrom, dateTo, iface, ifaceType })
      .then((res) => handleApiResponseWithSuccess(res, "Számlák szinkronizálása elindítva"));
  }

  /**
   * Összeállított (egyedi szűrésű) lekérdezés indítása az interfészre — a "lekérdezés indítása
   * interfészre" felület (pl. bejövő számla request tool) küldi. A backend a `request` DTO-t a
   * változatlanul továbbítja az IfaceSync API-nak — a felhasználó a hívás fejlécében megy.
   */
  static customDownSyncInterface = withBusyIndication(
    (request: GeneralGetPurchaseInvoicesRequestDTO, iface: Iface, ifaceType: IfaceType): Promise<void> =>
      http
        .post<ApiResponse<void>>(`iface-sync/custom-down-sync/interface`, { request, iface, ifaceType })
        .then((res) => handleApiResponseWithSuccess(res, "Lekérdezés elindítva az interfészre"))
  );

  /**
   * A részletes szerkesztőben szerkesztett partner újraszinkronizálása — a selected-items
   * down-synctől elkülönített TF végpont. A backend a kapott DTO-ból mindig új köztes tábla
   * sort hoz létre (`UserModified=true`), majd az új sor azonosítójával meghívja az
   * IfaceSync API-t.
   */
  static downSyncEditedPartner = withBusyIndication(
    (partner: IfaceSyncPartnerDTO, iface: Iface): Promise<void> =>
      http
        .post<ApiResponse<void>>(`iface-sync/down-sync/edited-partner`, { partner, iface })
        .then((res) => handleApiResponseWithSuccess(res, "Partner újraszinkronizálása elindítva"))
  );

  /**
   * A részletes szerkesztőben szerkesztett számlafej újraszinkronizálása — az edited-partner
   * végpont számlás párja. A backend a kapott DTO-ból mindig új köztes tábla fej sort hoz létre
   * (`UserModified=true`), a tételeket a `modifiedItems` overlay-jel másolja az új fej alá,
   * majd az új fej azonosítójával meghívja az IfaceSync API-t.
   */
  static downSyncEditedInvoiceHead = withBusyIndication(
    (invoiceHead: IfaceSyncInvoiceHeadDTO, iface: Iface, ifaceType: IfaceType): Promise<void> =>
      http
        .post<ApiResponse<void>>(`iface-sync/down-sync/edited-invoice-head`, { invoiceHead, iface, ifaceType })
        .then((res) => handleApiResponseWithSuccess(res, "Számla újraszinkronizálása elindítva"))
  );

  /**
   * Kijelölt elemek kihagyása a szinkron folyamatból. A backend minden sorhoz új köztes tábla
   * sort hoz létre `UserModified=true`-val és Skip (5) státusszal. Számlánál fej + tételek is
   * másolódnak. Nincs külső IfaceSync API hívás — mindent a TruFinance backend végez.
   */
  static markSelectedItemsSkipped = withBusyIndication(
    (items: any[], iface: Iface, ifaceType: IfaceType): Promise<void> =>
      http
        .post<ApiResponse<void>>(`iface-sync/mark-selected-items-skipped`, { items, iface, ifaceType })
        .then((res) => handleApiResponseWithSuccess(res, "Kijelölt tételek kihagyva a szinkron folyamatból"))
  );

  /**
   * Szinkron megjegyzés beállítása a kijelölt elemekre. A backend csak az adott sor
   * `sync_remark` mezőjét írja felül — nincs új sor, nincs külső API hívás.
   */
  static setSyncRemarkOnSelectedItems = withBusyIndication(
    (ids: number[], remark: string, ifaceType: IfaceType): Promise<void> =>
      http
        .post<ApiResponse<void>>(`iface-sync/set-sync-remark-on-selected-items`, { ids, remark, ifaceType })
        .then((res) => handleApiResponseWithSuccess(res, "Megjegyzés beállítva a kijelölt tételekre"))
  );
}
